import React, { useState, useEffect } from 'react';
import { Phone, Mail, Menu, X } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <header className={`fixed w-full top-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-white shadow-lg' : 'bg-white/95 backdrop-blur-sm'
    }`}>
      {/* Top Bar */}
      <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white py-2">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center md:justify-end items-center gap-4 md:gap-6 text-sm">
            <a href="tel:4036044218" className="flex items-center gap-2 hover:text-[#00AEEF] transition-colors">
              <Phone className="w-4 h-4" />
              <span>403-604-4218</span>
            </a>
            <a href="tel:4034703018" className="flex items-center gap-2 hover:text-[#00AEEF] transition-colors">
              <Phone className="w-4 h-4" />
              <span>403-470-3018</span>
            </a>
            <a href="mailto:info@sat-drywall.com" className="flex items-center gap-2 hover:text-[#00AEEF] transition-colors">
              <Mail className="w-4 h-4" />
              <span>info@sat-drywall.com</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between py-4">
          {/* Logo */}
          <Link to="/" className="flex items-center cursor-pointer">
            <img 
              src="https://customer-assets.emergentagent.com/job_wall-works-1/artifacts/t9oeun2w_IMG_5700.jpeg" 
              alt="Sat Drywall Ltd" 
              className="h-12 md:h-16 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            <Link 
              to="/" 
              className={`font-medium transition-colors ${isActive('/') ? 'text-[#00AEEF]' : 'text-gray-700 hover:text-[#00AEEF]'}`}
            >
              Home
            </Link>
            <Link 
              to="/services" 
              className={`font-medium transition-colors ${isActive('/services') ? 'text-[#00AEEF]' : 'text-gray-700 hover:text-[#00AEEF]'}`}
            >
              Services
            </Link>
            <Link 
              to="/about" 
              className={`font-medium transition-colors ${isActive('/about') ? 'text-[#00AEEF]' : 'text-gray-700 hover:text-[#00AEEF]'}`}
            >
              About Us
            </Link>
            <Link 
              to="/faq" 
              className={`font-medium transition-colors ${isActive('/faq') ? 'text-[#00AEEF]' : 'text-gray-700 hover:text-[#00AEEF]'}`}
            >
              FAQ
            </Link>
            <Link 
              to="/testimonials" 
              className={`font-medium transition-colors ${isActive('/testimonials') ? 'text-[#00AEEF]' : 'text-gray-700 hover:text-[#00AEEF]'}`}
            >
              Testimonials
            </Link>
            <Link 
              to="/contact" 
              className={`font-medium transition-colors ${isActive('/contact') ? 'text-[#00AEEF]' : 'text-gray-700 hover:text-[#00AEEF]'}`}
            >
              Contact
            </Link>
            <Link 
              to="/contact" 
              className="bg-[#00AEEF] text-white px-6 py-2.5 rounded-lg font-semibold hover:bg-[#0096d1] transition-all duration-300 hover:shadow-lg hover:scale-105"
            >
              Get a Quote
            </Link>
          </nav>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden text-gray-700 hover:text-[#00AEEF] transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <nav className="md:hidden pb-4 animate-in slide-in-from-top duration-300">
            <div className="flex flex-col gap-4">
              <Link 
                to="/" 
                onClick={closeMobileMenu}
                className={`font-medium transition-colors text-left py-2 ${isActive('/') ? 'text-[#00AEEF]' : 'text-gray-700 hover:text-[#00AEEF]'}`}
              >
                Home
              </Link>
              <Link 
                to="/services" 
                onClick={closeMobileMenu}
                className={`font-medium transition-colors text-left py-2 ${isActive('/services') ? 'text-[#00AEEF]' : 'text-gray-700 hover:text-[#00AEEF]'}`}
              >
                Services
              </Link>
              <Link 
                to="/about" 
                onClick={closeMobileMenu}
                className={`font-medium transition-colors text-left py-2 ${isActive('/about') ? 'text-[#00AEEF]' : 'text-gray-700 hover:text-[#00AEEF]'}`}
              >
                About Us
              </Link>
              <Link 
                to="/faq" 
                onClick={closeMobileMenu}
                className={`font-medium transition-colors text-left py-2 ${isActive('/faq') ? 'text-[#00AEEF]' : 'text-gray-700 hover:text-[#00AEEF]'}`}
              >
                FAQ
              </Link>
              <Link 
                to="/testimonials" 
                onClick={closeMobileMenu}
                className={`font-medium transition-colors text-left py-2 ${isActive('/testimonials') ? 'text-[#00AEEF]' : 'text-gray-700 hover:text-[#00AEEF]'}`}
              >
                Testimonials
              </Link>
              <Link 
                to="/contact" 
                onClick={closeMobileMenu}
                className={`font-medium transition-colors text-left py-2 ${isActive('/contact') ? 'text-[#00AEEF]' : 'text-gray-700 hover:text-[#00AEEF]'}`}
              >
                Contact
              </Link>
              <Link 
                to="/contact" 
                onClick={closeMobileMenu}
                className="bg-[#00AEEF] text-white px-6 py-2.5 rounded-lg font-semibold hover:bg-[#0096d1] transition-all duration-300 text-left"
              >
                Get a Quote
              </Link>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
